import { MessageSquare, Send, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TaskComment } from '../types/task';
import { ConfirmDialog } from './ui/alert-dialog-helper';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface MentionEntry {
  id: string;
  name: string;
}

interface TaskCommentsSectionProps {
  taskId: string;
  comments: TaskComment[];
  currentUser: User | null;
  onAdd: (comment: string, mentions?: string[]) => Promise<void>;
  onDelete?: (commentId: string) => Promise<void>;
}

const TaskCommentsSection: React.FC<TaskCommentsSectionProps> = ({
  taskId,
  comments,
  currentUser,
  onAdd,
  onDelete,
}) => {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<TaskComment | null>(null);

  // Mention state
  const [users, setUsers] = useState<User[]>([]);
  const [mentionedUsers, setMentionedUsers] = useState<Map<string, MentionEntry>>(new Map());
  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIdx, setMentionStartIdx] = useState(-1);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load users once
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/users?is_active=true&limit=100', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.users ?? data.data ?? []);
        setUsers(list);
      } catch {
        // silently fail - mentions just won't work
      }
    };
    loadUsers();
  }, []);

  const filteredUsers = users.filter(u => {
    if (!mentionQuery) return true;
    const q = mentionQuery.toLowerCase();
    return (
      u.first_name.toLowerCase().includes(q) ||
      u.last_name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q)
    );
  }).slice(0, 6);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart ?? value.length;
    setNewComment(value);

    // Look backwards from cursor to find an active @ trigger
    const textBefore = value.slice(0, cursorPos);
    const atIdx = textBefore.lastIndexOf('@');

    if (atIdx === -1) {
      setShowDropdown(false);
      return;
    }

    // The @ must be at the start or preceded by a space/newline
    if (atIdx > 0 && !/[\s\n]/.test(textBefore[atIdx - 1])) {
      setShowDropdown(false);
      return;
    }

    const query = textBefore.slice(atIdx + 1);

    // Dismiss if query contains a newline (user pressed enter past the @)
    if (query.includes('\n')) {
      setShowDropdown(false);
      return;
    }

    setMentionStartIdx(atIdx);
    setMentionQuery(query);
    setHighlightIdx(0);
    setShowDropdown(true);
  };

  const insertMention = useCallback((user: User) => {
    const name = `${user.first_name} ${user.last_name}`;
    const before = newComment.slice(0, mentionStartIdx);
    const ta = textareaRef.current;
    const cursorPos = ta?.selectionStart ?? newComment.length;
    const after = newComment.slice(cursorPos);

    const inserted = `${before}@${name} ${after}`;
    setNewComment(inserted);

    setMentionedUsers(prev => {
      const next = new Map(prev);
      next.set(name, { id: user.id, name });
      return next;
    });

    setShowDropdown(false);
    setMentionQuery('');
    setMentionStartIdx(-1);

    // Restore focus and cursor position after React re-render
    const newCursorPos = before.length + name.length + 2; // +2 for @ and space
    requestAnimationFrame(() => {
      if (ta) {
        ta.focus();
        ta.setSelectionRange(newCursorPos, newCursorPos);
      }
    });
  }, [newComment, mentionStartIdx]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showDropdown || filteredUsers.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => (i + 1) % filteredUsers.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => (i - 1 + filteredUsers.length) % filteredUsers.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(filteredUsers[highlightIdx]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowDropdown(false);
    }
  };

  const handleSubmit = async () => {
    if (!newComment.trim()) return;

    try {
      setIsSubmitting(true);

      // Collect mention IDs from tracked mentions that still appear in the text
      const mentions: string[] = [];
      mentionedUsers.forEach(({ id, name }) => {
        if (newComment.includes(`@${name}`)) mentions.push(id);
      });

      await onAdd(newComment.trim(), mentions.length > 0 ? mentions : undefined);
      setNewComment('');
      setMentionedUsers(new Map());
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (comment: TaskComment) => {
    setCommentToDelete(comment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!onDelete || !commentToDelete) return;

    try {
      await onDelete(commentToDelete.id);
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Render comment text with highlighted @mentions
  const renderCommentText = (text: string) => {
    const mentionPattern = /@([\w]+ [\w]+)/g;
    const parts: React.ReactNode[] = [];
    let lastIdx = 0;
    let m: RegExpExecArray | null;

    while ((m = mentionPattern.exec(text)) !== null) {
      if (m.index > lastIdx) {
        parts.push(text.slice(lastIdx, m.index));
      }
      parts.push(
        <span key={m.index} className="text-blue-600 font-medium bg-blue-50 rounded px-0.5">
          @{m[1]}
        </span>
      );
      lastIdx = m.index + m[0].length;
    }

    if (lastIdx < text.length) parts.push(text.slice(lastIdx));
    return parts.length > 0 ? parts : text;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Comments & Activity ({comments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
        {/* Add Comment Form */}
        <div className="space-y-2">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              placeholder="Add a comment... Type @ to mention someone"
              value={newComment}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              rows={3}
              className="resize-none"
            />

            {/* Mention Autocomplete Dropdown */}
            {showDropdown && filteredUsers.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute left-0 right-0 bottom-full mb-1 z-50 bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto"
              >
                {filteredUsers.map((u, idx) => (
                  <button
                    key={u.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                      idx === highlightIdx ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-xs font-medium text-primary">
                        {u.first_name.charAt(0)}{u.last_name.charAt(0)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{u.first_name} {u.last_name}</p>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={!newComment.trim() || isSubmitting}
              size="sm"
            >
              <Send className="h-4 w-4 mr-2" />
              Post Comment
            </Button>
          </div>
        </div>

        {/* Comments List */}
        <div className="space-y-4 pt-4 border-t">
          {comments.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No comments yet. Be the first to comment!
            </div>
          )}

          {comments.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">
                    {comment.user?.first_name?.charAt(0)}{comment.user?.last_name?.charAt(0)}
                  </span>
                </div>
              </div>

              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      {comment.user?.first_name} {comment.user?.last_name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(comment.created_at)}
                    </span>
                    {comment.updated_at !== comment.created_at && (
                      <span className="text-xs text-muted-foreground italic">(edited)</span>
                    )}
                  </div>

                  {currentUser && comment.user_id === currentUser.id && onDelete && (
                    <button
                      onClick={() => handleDeleteClick(comment)}
                      className="text-muted-foreground hover:text-destructive transition-colors focus:outline-none focus:ring-2 focus:ring-destructive rounded p-1"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>

                <div className="text-sm whitespace-pre-wrap break-words">
                  {renderCommentText(comment.comment)}
                </div>

                {comment.mentions && comment.mentions.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    Mentioned {comment.mentions.length} user(s)
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Comment"
        description="Are you sure you want to delete this comment? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />
    </>
  );
};

export default TaskCommentsSection;
